import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '../context/ToastContext';

const NewExpense = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    amount: '',
    category: 'Travel',
    expenseDate: new Date().toISOString().split('T')[0],
    paymentType: 'reimbursement',
  });
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const fileInputRef = useRef(null);
  const toast = useToast();
  const navigate = useNavigate();

  const categories = ['Travel', 'Food', 'Accommodation', 'Office Supplies', 'Client Entertainment', 'Training', 'Other'];
  const paymentTypes = [
    { value: 'reimbursement', label: 'Reimbursement (after approval)' },
    { value: 'instant', label: 'Instant Payment' },
    { value: 'advance', label: 'Advance Payment' },
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleFileSelect = (selectedFiles) => {
    const newFiles = Array.from(selectedFiles).filter(file => {
      const isValidType = file.type.startsWith('image/') || file.type === 'application/pdf';
      const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB
      
      if (!isValidType) {
        toast.error(`${file.name} is not a valid file type. Only images and PDFs are allowed.`);
        return false;
      }
      if (!isValidSize) {
        toast.error(`${file.name} exceeds 10MB limit.`);
        return false;
      }
      return true;
    });

    setFiles(prev => [...prev, ...newFiles].slice(0, 5));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.title || !formData.description || !formData.amount || !formData.expenseDate) {
      return setError('Please fill in all required fields');
    }

    if (parseFloat(formData.amount) <= 0) {
      return setError('Amount must be greater than 0');
    }

    setLoading(true);

    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => data.append(key, formData[key]));
      files.forEach(file => data.append('attachments', file));

      await axios.post('/api/expenses', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Expense report submitted successfully!');
      navigate('/expenses');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit expense');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Submit New Expense</h1>
          <p className="page-subtitle">Fill in the details and attach proof of your expense</p>
        </div>
      </div>

      <div className="card" style={{ maxWidth: '800px' }}>
        <div className="card-body">
          {error && <div className="alert alert-error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Expense Title *</label>
              <input
                type="text"
                name="title"
                className="form-input"
                placeholder="e.g., Client lunch meeting"
                value={formData.title}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description *</label>
              <textarea
                name="description"
                className="form-textarea"
                placeholder="Describe the expense in detail..."
                value={formData.description}
                onChange={handleChange}
                required
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Amount (USD) *</label>
                <input
                  type="number"
                  name="amount"
                  className="form-input"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Expense Date *</label>
                <input
                  type="date"
                  name="expenseDate"
                  className="form-input"
                  value={formData.expenseDate}
                  onChange={handleChange}
                  max={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Category</label>
                <select
                  name="category"
                  className="form-select"
                  value={formData.category}
                  onChange={handleChange}
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Payment Type</label>
                <select
                  name="paymentType"
                  className="form-select"
                  value={formData.paymentType}
                  onChange={handleChange}
                >
                  {paymentTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
                <div className="form-hint">
                  {formData.paymentType === 'advance' && 'Request payment before the expense occurs'}
                  {formData.paymentType === 'instant' && 'Mark as needing immediate payment'}
                  {formData.paymentType === 'reimbursement' && 'Standard reimbursement after approval'}
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Receipt / Proof of Expense</label>
              <div
                className={`file-upload ${dragging ? 'dragging' : ''}`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  onChange={(e) => handleFileSelect(e.target.files)}
                />
                <svg className="file-upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <div className="file-upload-text">
                  Click to upload or drag and drop
                </div>
                <div className="file-upload-hint">
                  Images or PDF (max 5 files, 10MB each)
                </div>
              </div>

              {files.length > 0 && (
                <div className="file-list">
                  {files.map((file, index) => (
                    <div key={index} className="file-item">
                      <div className="file-item-icon">
                        {file.type === 'application/pdf' ? (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <polyline points="21 15 16 10 5 21" />
                          </svg>
                        )}
                      </div>
                      <div className="file-item-info">
                        <div className="file-item-name">{file.name}</div>
                        <div className="file-item-size">{formatFileSize(file.size)}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="btn btn-outline btn-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button type="button" onClick={() => navigate('/expenses')} className="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Expense Report'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NewExpense;
